import { Control, type ControlOptions } from "../core/Control.js";
import type { Rect, Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import type { ColorRef } from "../art/ArtControl.js";
import { getLook, lookContext } from "../art/looks.js";

export interface ProgressBarOptions extends ControlOptions {
  value?: number;
  minimum?: number;
  maximum?: number;
  /** Sweep continuously instead of showing a value. */
  indeterminate?: boolean;
  /** Draw the completed percentage over the bar. */
  showText?: boolean;
  /** Override the caption; receives the value and the 0..1 fraction. */
  textFormat?: (value: number, fraction: number) => string;
  font?: FontSpec;
  /** Seconds for one sweep of the indeterminate marquee. */
  sweepSeconds?: number;
  /** A registered look to paint instead of the theme's bar: `flare`. */
  look?: string | null;
  /** The look's color; the theme's accent when absent. */
  lookColor?: ColorRef | null;
}

/**
 * A horizontal progress bar, determinate or indeterminate.
 *
 * The theme owns the look — Classic paints Win95 segments where the flat
 * themes paint a continuous fill — so this control is really a value, a clock
 * for the marquee, and the caption on top.
 *
 * Vertical bars are not supported; they are rare enough in Windows UI that
 * carrying the extra axis through every theme is not worth it.
 */
export class ProgressBar extends Control {
  #value: number;
  #minimum: number;
  #maximum: number;
  #indeterminate: boolean;
  #animating = false;

  showText: boolean;
  textFormat: ((value: number, fraction: number) => string) | null;
  font: FontSpec | null;
  sweepSeconds: number;
  #look: string | null;
  lookColor: ColorRef | null;

  constructor(options: ProgressBarOptions = {}) {
    super({ width: 200, height: 20, tabStop: false, ...options });
    this.#minimum = options.minimum ?? 0;
    this.#maximum = options.maximum ?? 100;
    this.#value = this.#clamp(options.value ?? 0);
    this.#indeterminate = options.indeterminate ?? false;
    this.showText = options.showText ?? false;
    this.textFormat = options.textFormat ?? null;
    this.font = options.font ?? null;
    this.sweepSeconds = options.sweepSeconds ?? 1.6;
    this.#look = options.look ?? null;
    this.lookColor = options.lookColor ?? null;
    this.#syncAnimation();
  }

  #clamp(v: number): number {
    if (!Number.isFinite(v)) return this.#minimum;
    return Math.min(this.#maximum, Math.max(this.#minimum, v));
  }

  get value(): number {
    return this.#value;
  }
  set value(v: number) {
    const next = this.#clamp(v);
    if (next === this.#value) return;
    this.#value = next;
    this.invalidate();
    this.emit("valuechanged");
  }

  get minimum(): number {
    return this.#minimum;
  }
  set minimum(v: number) {
    this.#minimum = v;
    if (this.#maximum < v) this.#maximum = v;
    this.value = this.#clamp(this.#value);
    this.invalidate();
  }

  get maximum(): number {
    return this.#maximum;
  }
  set maximum(v: number) {
    this.#maximum = v;
    if (this.#minimum > v) this.#minimum = v;
    this.value = this.#clamp(this.#value);
    this.invalidate();
  }

  /** Completed proportion, 0..1. Zero when the range is empty. */
  get fraction(): number {
    const span = this.#maximum - this.#minimum;
    return span <= 0 ? 0 : (this.#value - this.#minimum) / span;
  }

  get indeterminate(): boolean {
    return this.#indeterminate;
  }
  set indeterminate(v: boolean) {
    if (this.#indeterminate === v) return;
    this.#indeterminate = v;
    this.#syncAnimation();
    this.invalidate();
  }

  /** Move by `delta`, clamped. The usual way to drive a determinate bar. */
  step(delta = 1): void {
    this.value = this.#value + delta;
  }

  protected override onEnvironmentChanged(): void {
    // Gaining or losing a desktop changes where the animator registers.
    this.#animating = false;
    this.#syncAnimation();
  }

  /**
   * A registered look to paint instead of the theme's bar, or null for the theme's. A name nobody
   * registered falls back to the theme.
   */
  get look(): string | null {
    return this.#look;
  }
  set look(v: string | null) {
    this.#look = v;
    this.#syncAnimation();
    this.invalidate();
  }

  override get paintOverflow(): number {
    // The flare at the leading edge reaches past the bar.
    return Math.max(super.paintOverflow, getLook(this.#look)?.progress ? this.height * 2 + 4 : 0);
  }

  /** Only an indeterminate bar, or one wearing a moving look, needs a frame every tick. */
  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const look = getLook(this.#look);
    const moving =
      look?.progress !== undefined &&
      (look.animates?.({ state: this.visualState(), lit: false }, "progress") ?? false) &&
      !desktop.reducedMotion;
    const want = (this.#indeterminate || moving) && this.effectivelyVisible;
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  /** Keeps the height it was given and takes the width it is offered. */
  override preferredSize(available: Size): Size {
    return { width: available.width, height: this.height };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    this.#syncAnimation();
    const now = this.desktop?.frameTime ?? 0;
    const phase = this.sweepSeconds > 0 ? (now % this.sweepSeconds) / this.sweepSeconds : 0;

    const visual = {
      fraction: this.fraction,
      indeterminate: this.#indeterminate,
      phase,
      disabled: !this.effectivelyEnabled,
    };
    const look = getLook(this.#look);
    if (look?.progress) look.progress(lookContext(this, rc, theme, this.lookColor, false), visual);
    else theme.drawProgressBar(rc, this.localRect, visual);

    if (!this.showText || this.#indeterminate) return;
    const caption = this.textFormat
      ? this.textFormat(this.#value, this.fraction)
      : `${Math.round(this.fraction * 100)}%`;
    if (!caption) return;

    const r = this.localRect;
    const font = this.font ?? theme.metrics.font;
    if (!this.effectivelyEnabled) {
      rc.drawTextCentered(caption, r, font, theme.palette.textDisabled);
      return;
    }

    // Draw the caption twice, clipped either side of the fill edge, so it
    // stays legible where it crosses onto the filled part. Windows does the
    // same. The split is approximate for a theme that paints segments, which
    // is close enough to read correctly.
    const split = r.x + 1 + (r.width - 2) * Math.min(1, Math.max(0, this.fraction));
    const filled: Rect = { x: r.x, y: r.y, width: Math.max(0, split - r.x), height: r.height };
    const rest: Rect = { x: split, y: r.y, width: Math.max(0, r.x + r.width - split), height: r.height };

    if (filled.width > 0) {
      rc.clipped(filled, () => rc.drawTextCentered(caption, r, font, theme.palette.accentText));
    }
    if (rest.width > 0) {
      rc.clipped(rest, () => rc.drawTextCentered(caption, r, font, theme.palette.text));
    }
  }
}
